import { useEffect, useState, type ReactNode } from "react"
import { Menu, MessageSquare, Rocket, Check } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "../Button/Button"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../Tooltip/Tooltip"

function useIsBelow(bp: number) {
  const [below, setBelow] = useState(
    typeof window !== "undefined" ? window.innerWidth < bp : false,
  )
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${bp - 1}px)`)
    setBelow(mq.matches)
    const handler = (e: MediaQueryListEvent) => setBelow(e.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [bp])
  return below
}

export interface NavbarProps {
  isMobile?: boolean
  isPhone?: boolean
  onMenuClick?: () => void
  showBorder?: boolean
  tabsSlot?: ReactNode
  modeToggleSlot?: ReactNode
  showChatButton?: boolean
  onShowChat?: () => void
  showDeploy?: boolean
  deployVersion?: string
  deployed?: boolean
  onDeploy?: () => void
  className?: string
}

export function Navbar({
  isMobile = false,
  isPhone = false,
  onMenuClick,
  showBorder = false,
  tabsSlot,
  modeToggleSlot,
  showChatButton = false,
  onShowChat,
  showDeploy = false,
  deployVersion = "v1",
  deployed = false,
  onDeploy,
  className,
}: NavbarProps) {
  const isCompact = useIsBelow(680)
  const isIconOnly = useIsBelow(481)

  return (
    <nav
      className={cn(
        "h-11 shrink-0 flex items-center bg-card px-4",
        showBorder && "border-b border-border",
        className,
      )}
    >
      {isMobile && onMenuClick && (
        <button
          type="button"
          onClick={onMenuClick}
          title="Open menu"
          aria-label="Open menu"
          className="flex items-center p-0 mr-3 border-0 bg-transparent text-muted-foreground hover:text-foreground cursor-pointer"
        >
          <Menu size={15} strokeWidth={1.5} />
        </button>
      )}

      {tabsSlot}
      <div className="flex items-center gap-1 pl-3 flex-1 min-w-0">
        {modeToggleSlot && <div className="ml-auto">{modeToggleSlot}</div>}
      </div>

      {(showChatButton || showDeploy) && (
        <div className={cn("flex items-center gap-2", showDeploy && "ml-1")}>
          {showChatButton && onShowChat && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onShowChat}
              title="Show chat"
              aria-label="Show chat"
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground hover:bg-muted"
            >
              <MessageSquare size={14} strokeWidth={1.5} />
            </Button>
          )}
          {showDeploy &&
            (deployed ? (
              isIconOnly ? (
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        tabIndex={0}
                        aria-label={`Deployed ${deployVersion}`}
                        className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-muted text-caption text-muted-foreground select-none"
                      >
                        <Check size={12} strokeWidth={2.25} className="text-foreground" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>Deployed {deployVersion}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <span
                  aria-label={`Deployed ${deployVersion}`}
                  className="inline-flex items-center gap-1.5 h-7 px-3 rounded-md bg-muted text-caption text-muted-foreground select-none"
                  title={isPhone ? `Deployed ${deployVersion}` : undefined}
                >
                  <Check size={11} strokeWidth={2.25} className="text-foreground" />
                  {!isPhone && "Deployed"}
                  <span className="font-mono tabular-nums opacity-70">{deployVersion}</span>
                </span>
              )
            ) : isIconOnly ? (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      size="sm"
                      onClick={onDeploy}
                      className="h-7 min-h-7 px-2 text-caption"
                      aria-label={`Deploy ${deployVersion}`}
                    >
                      <Rocket size={10} strokeWidth={2} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Deploy {deployVersion}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Button
                type="button"
                size="sm"
                onClick={onDeploy}
                className={cn("h-7 min-h-7 gap-1.5 text-caption", isCompact ? "px-2" : "px-3")}
                title={isPhone ? `Deploy ${deployVersion}` : undefined}
              >
                <Rocket size={10} strokeWidth={2} />
                {!isPhone && "Deploy"}
                <span className="font-mono tabular-nums opacity-70">{deployVersion}</span>
              </Button>
            ))}
        </div>
      )}
    </nav>
  )
}
